import { useEffect, useRef, useState } from "react";

import { IgAutocomplete } from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import { useDispatch, useSelector } from "@src/store";
import { ExperienceBuilderAiSelector } from "@src/store/slices/experienceBuilder/ai";
import { ExperienceBuilderAction } from "@src/store/slices/experienceBuilder";

import { useNavigatorContext } from "../context/useNavigatorContext";
import { NavigationItem } from "../types";

import { NavigatorGroup } from "./NavigatorGroup";
import { NavigatorInput } from "./NavigatorInput";
import { NavigatorOption } from "./NavigatorOption";

export const NavigatorAutocomplete = () => {
  const dispatch = useDispatch();
  const isAiBusy = useSelector(ExperienceBuilderAiSelector.getIsAiBusy);
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on click outside — blur is unreliable due to MUI Dialog focus trap
  // and iframe focus stealing
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const popper = document.querySelector(".MuiAutocomplete-popper");
      if (
        rootRef.current?.contains(e.target as Node) ||
        popper?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const {
    options,
    selectedOption,
    inputValue,
    setInputValue,
    listboxRef,
    noOptionsText,
    isLoading,
  } = useNavigatorContext();

  return (
    <IgAutocomplete
      ref={rootRef}
      size="small"
      disabled={isAiBusy}
      disableClearable
      open={open}
      onOpen={() => setOpen(true)}
      onClose={(_e, reason) => {
        if (reason !== "blur") {
          setOpen(false);
        }
      }}
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_e, value, reason) => {
        if (reason === "input") {
          setInputValue(value);
        }
      }}
      onChange={(_e, value: NavigationItem | null) => {
        if (value) {
          void dispatch(ExperienceBuilderAction.setTargetUrl(value.path));
          rootRef.current?.querySelector("input")?.blur();
        }
      }}
      groupBy={(option: NavigationItem) => option.group}
      getOptionLabel={(option: NavigationItem) => option.title}
      isOptionEqualToValue={(a: NavigationItem, b: NavigationItem) =>
        a.path === b.path
      }
      filterOptions={(x: NavigationItem[]) => x}
      noOptionsText={noOptionsText}
      loading={isLoading}
      sx={{
        flex: 1.5,
        minWidth: 160,
        maxWidth: 320,
        "& .MuiAutocomplete-endAdornment": {
          position: "static",
          transform: "none",
        },
        "& .MuiOutlinedInput-root": {
          pr: "9px !important",
          height: 32,
          fontSize: 12,
        },
      }}
      slotProps={{
        popper: {
          placement: "bottom-start",
          modifiers: [{ name: "flip", enabled: false }],
          sx: {
            width: "auto !important",
            maxWidth: 500,
          },
        },
        listbox: {
          ref: listboxRef,
          sx: {
            maxHeight: 400,
            overflow: "auto",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 4,
            },
            "&:has([data-load-more]:hover) .MuiAutocomplete-option.Mui-focused":
              {
                backgroundColor: "transparent",
              },
            "& .standalone-group .MuiAutocomplete-option": {
              paddingLeft: 2,
            },
          },
        },
        paper: {
          elevation: 0,
          sx: {
            border: `1px solid ${theme.igBorders.core.default}`,
            borderRadius: "5px",
          },
        },
      }}
      // eslint-disable-next-line react/no-unstable-nested-components
      renderGroup={(params) => (
        <li key={params.key}>
          <NavigatorGroup params={params} />
        </li>
      )}
      // eslint-disable-next-line react/no-unstable-nested-components
      renderOption={(props, option: NavigationItem) => (
        <NavigatorOption
          key={String(props.key)}
          props={props}
          option={option}
        />
      )}
      // eslint-disable-next-line react/no-unstable-nested-components
      renderInput={(params) => <NavigatorInput params={params} />}
    />
  );
};
